import React, { useState, useEffect } from 'react';
import { BedrockClient, ListFoundationModelsCommand } from '@aws-sdk/client-bedrock';
import { BedrockRuntimeClient, ConverseCommand } from '@aws-sdk/client-bedrock-runtime';
import { BedrockAgentClient, ListKnowledgeBasesCommand } from '@aws-sdk/client-bedrock-agent';
import { BedrockAgentRuntimeClient, RetrieveAndGenerateCommand } from '@aws-sdk/client-bedrock-agent-runtime';
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, PutCommand, QueryCommand, DeleteCommand } from '@aws-sdk/lib-dynamodb';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { jwtVerify, importJWK } from 'jose';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import { CONFIG } from './config';
import './App.css';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [messages, setMessages] = useState([]);
  const [currentChatId, setCurrentChatId] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState('');
  const [kbLoading, setKbLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingChatName, setEditingChatName] = useState('');

  useEffect(() => {
    const initializeAuth = async () => {
      // Check for stored authentication on app load
      const storedUser = localStorage.getItem('mbk_user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const payload = await verifyToken(userData.token);
          if (payload) {
            setAuthenticated(true);
            setUser(userData);
            return;
          } else {
            localStorage.removeItem('mbk_user');
          }
        } catch (error) {
          localStorage.removeItem('mbk_user');
        }
      }

      // Handle OAuth callback - code flow returns authorization code in URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const authCode = urlParams.get('code');
      
      if (authCode) {
        // Exchange authorization code for tokens
        exchangeCodeForTokens(authCode);
      }
    };

    initializeAuth();
  }, []); // Remove dependencies to run only once

  useEffect(() => {
    if (authenticated && user) {
      initializeClients();
    }
  }, [authenticated, user]);

  useEffect(() => {
    if (clients) {
      loadModels();
      loadKnowledgeBases();
    }
  }, [clients]);

  useEffect(() => {
    if (clients) {
      loadModels();
      loadChatHistory();
      setCurrentChatId(generateChatId());
    }
  }, [clients]);

  const generateChatId = () => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };

  const initializeClients = () => {
    const credentials = fromCognitoIdentityPool({
      clientConfig: { region: CONFIG.AWS_REGION },
      identityPoolId: CONFIG.IDENTITY_POOL_ID,
      logins: {
        [`cognito-idp.${CONFIG.AWS_REGION}.amazonaws.com/${CONFIG.USER_POOL_ID}`]: user.token
      }
    });

    const finalCredentials = CONFIG.AWS_CREDENTIALS.accessKeyId ? CONFIG.AWS_CREDENTIALS : credentials;
    const config = { region: CONFIG.AWS_REGION, credentials: finalCredentials };
    
    setClients({
      bedrock: new BedrockClient(config),
      bedrockRuntime: new BedrockRuntimeClient(config),
      bedrockAgent: new BedrockAgentClient(config),
      bedrockAgentRuntime: new BedrockAgentRuntimeClient(config),
      dynamodb: DynamoDBDocumentClient.from(new DynamoDBClient(config)),
      s3: new S3Client(config)
    });
  };

  const handleTokenReceived = async (idToken) => {
    const payload = await verifyToken(idToken);
    if (payload) {
      const userData = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        token: idToken
      };
      
      setAuthenticated(true);
      setUser(userData);
      
      // Store user data in localStorage
      localStorage.setItem('mbk_user', JSON.stringify(userData));
      
      // Clear URL hash
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const verifyToken = async (token) => {
    try {
      const jwksUrl = `https://cognito-idp.${CONFIG.AWS_REGION}.amazonaws.com/${CONFIG.USER_POOL_ID}/.well-known/jwks.json`;
      const response = await fetch(jwksUrl);
      const jwks = await response.json();
      
      const header = JSON.parse(atob(token.split('.')[0]));
      const key = jwks.keys.find(k => k.kid === header.kid);
      
      if (!key) return null;
      
      const jwk = await importJWK(key);
      const { payload } = await jwtVerify(token, jwk, { audience: CONFIG.CLIENT_ID });
      return payload;
    } catch (error) {
      console.error('Token verification failed:', error);
      return null;
    }
  };

  const exchangeCodeForTokens = async (authCode) => {
    try {
      const tokenEndpoint = `${CONFIG.COGNITO_DOMAIN}/oauth2/token`;
      const redirectUri = window.location.origin + '/';
      
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: CONFIG.CLIENT_ID,
          code: authCode,
          redirect_uri: redirectUri,
        }),
      });

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.status}`);
      }

      const tokens = await response.json();
      
      if (tokens.id_token) {
        handleTokenReceived(tokens.id_token);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
    }
  };

  const handleLogin = () => {
    const redirectUri = encodeURIComponent(window.location.origin + '/');
    const authUrl = `${CONFIG.COGNITO_DOMAIN}/oauth2/authorize?client_id=${CONFIG.CLIENT_ID}&response_type=code&scope=email+openid+profile+phone&redirect_uri=${redirectUri}`;
    window.location.href = authUrl;
  };

  const loadKnowledgeBases = async () => {
    setKbLoading(true);
    try {
      const command = new ListKnowledgeBasesCommand({});
      const response = await clients.bedrockAgent.send(command);
      setKnowledgeBases(response.knowledgeBaseSummaries || []);
    } catch (error) {
      console.error('Error loading knowledge bases:', error);
      setKnowledgeBases([]);
    } finally {
      setKbLoading(false);
    }
  };

  const loadModels = async () => {
    setModelsLoading(true);
    try {
      const command = new ListFoundationModelsCommand({});
      const response = await clients.bedrock.send(command);
      const textModels = response.modelSummaries.filter(model => 
        model.inputModalities.includes('TEXT') &&
        model.outputModalities.includes('TEXT') &&
        model.modelLifecycle.status === 'ACTIVE' &&
        model.inferenceTypesSupported?.includes('ON_DEMAND')
      );
      setModels(textModels);
      if (textModels.length > 0) {
        setSelectedModel(textModels[0].modelId);
      }
    } catch (error) {
      console.error('Error loading models:', error);
    } finally {
      setModelsLoading(false);
    }
  };

  const invokeModel = async (modelId, currentMessages, retryCount = 0) => {
    try {
      // Convert messages to Converse API format
      const converseMessages = currentMessages.map(msg => ({
        role: msg.role,
        content: [{ text: msg.content }]
      }));

      const command = new ConverseCommand({
        modelId,
        messages: converseMessages,
        inferenceConfig: {
          maxTokens: 1000,
          temperature: 0.7
        }
      });

      const response = await clients.bedrockRuntime.send(command);
      return response.output.message.content[0].text;
    } catch (error) {
      if (error.name === 'ThrottlingException' && retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return invokeModel(modelId, currentMessages, retryCount + 1);
      }
      return `Error: ${error.message}`;
    }
  };

  const queryKnowledgeBase = async (knowledgeBaseId, query, retryCount = 0) => {
    try {
      const command = new RetrieveAndGenerateCommand({
        input: {
          text: query
        },
        retrieveAndGenerateConfiguration: {
          type: 'KNOWLEDGE_BASE',
          knowledgeBaseConfiguration: {
            knowledgeBaseId: knowledgeBaseId,
            modelArn: `arn:aws:bedrock:${CONFIG.AWS_REGION}::foundation-model/${selectedModel}`
          }
        }
      });

      const response = await clients.bedrockAgentRuntime.send(command);
      return response.output.text;
    } catch (error) {
      if (error.name === 'ThrottlingException' && retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return queryKnowledgeBase(knowledgeBaseId, query, retryCount + 1);
      }
      console.error('Knowledge base query error:', error);
      return `Error querying knowledge base: ${error.message}`;
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setLoading(true);

    try {
      let content = '';
      
      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        content = await file.text();
      } else if (file.name.endsWith('.md')) {
        content = await file.text();
      } else if (file.type === 'text/html' || file.name.endsWith('.html')) {
        const htmlContent = await file.text();
        content = htmlContent.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
      } else if (file.type === 'application/pdf') {
        content = await extractPdfText(file);
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name.endsWith('.docx')) {
        const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
        content = result.value;
      } else if (file.type === 'application/msword' || file.name.endsWith('.doc')) {
        content = 'DOC files require conversion to DOCX format for processing';
      } else {
        content = 'Unsupported file type';
      }

      setFileContent(content);
      
      // Upload to S3
      await uploadToS3(file, content);
      
    } catch (error) {
      console.error('Error processing file:', error);
      setFileContent('Error processing file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const extractPdfText = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let text = '';
      
      for (let i = 1; i <= Math.min(pdf.numPages, 10); i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        text += pageText + '\n';
      }
      
      return text || 'No text content found in PDF';
    } catch (error) {
      console.error('PDF processing error:', error);
      return 'Error processing PDF: ' + error.message;
    }
  };

  const uploadToS3 = async (file, content) => {
    try {
      const key = `${user.id}/${Date.now()}-${file.name}`;
      const command = new PutObjectCommand({
        Bucket: CONFIG.S3_BUCKET,
        Key: key,
        Body: file,
        ContentType: file.type,
        Metadata: {
          'extracted-content': content.substring(0, 1000) // Store first 1000 chars as metadata
        }
      });
      
      await clients.s3.send(command);
      console.log('File uploaded to S3:', key);
    } catch (error) {
      console.error('Error uploading to S3:', error);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setFileContent('');
  };

  const startEditingChat = (chatId, currentName) => {
    setEditingChatId(chatId);
    setEditingChatName(currentName);
  };

  const saveChatName = async () => {
    if (!editingChatName.trim()) return;
    
    try {
      const chat = chatHistory.find(c => c.chat_id === editingChatId);
      const command = new PutCommand({
        TableName: CONFIG.DYNAMODB_TABLE,
        Item: {
          ...chat,
          chat_name: editingChatName.trim()
        }
      });
      await clients.dynamodb.send(command);
      
      setChatHistory(prev => prev.map(chat => 
        chat.chat_id === editingChatId 
          ? { ...chat, chat_name: editingChatName.trim() }
          : chat
      ));
      
      setEditingChatId(null);
      setEditingChatName('');
    } catch (error) {
      console.error('Error renaming chat:', error);
    }
  };

  const cancelEditingChat = () => {
    setEditingChatId(null);
    setEditingChatName('');
  };

  const saveChatHistory = async () => {
    try {
      // Find existing chat to preserve custom name
      const existingChat = chatHistory.find(chat => chat.chat_id === currentChatId);
      const chatName = existingChat?.chat_name || 
        (messages.length > 0 
          ? messages[0].content.substring(0, 30) + (messages[0].content.length > 30 ? '...' : '')
          : 'New Chat');
        
      const command = new PutCommand({
        TableName: CONFIG.DYNAMODB_TABLE,
        Item: {
          user_id: user.id,
          chat_id: currentChatId,
          chat_name: chatName,
          messages,
          timestamp: new Date().toISOString()
        }
      });
      await clients.dynamodb.send(command);
    } catch (error) {
      console.error('Error saving chat:', error);
    }
  };

  const loadChatHistory = async () => {
    try {
      const command = new QueryCommand({
        TableName: CONFIG.DYNAMODB_TABLE,
        KeyConditionExpression: 'user_id = :userId',
        ExpressionAttributeValues: { ':userId': user.id }
      });
      const response = await clients.dynamodb.send(command);
      setChatHistory(response.Items || []);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;
    
    // Show only the user's question in the chat bubble
    const userMessage = { role: 'user', content: inputMessage };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputMessage('');
    setLoading(true);

    // Add small delay to prevent rate limiting
    await new Promise(resolve => setTimeout(resolve, 500));

    let response;
    
    // Use knowledge base if selected, otherwise use regular conversation
    if (selectedKnowledgeBase) {
      response = await queryKnowledgeBase(selectedKnowledgeBase, inputMessage);
    } else {
      // Prepare messages for AI with file content included if available
      let messagesForAI = [...newMessages];
      if (fileContent) {
        // Replace the last user message with one that includes file content for AI processing
        messagesForAI[messagesForAI.length - 1] = {
          role: 'user',
          content: `File content:\n${fileContent}\n\nUser question: ${inputMessage}`
        };
      }

      // Pass conversation history to maintain context
      response = await invokeModel(selectedModel, messagesForAI);
    }
    
    const assistantMessage = { role: 'assistant', content: response };
    
    const finalMessages = [...newMessages, assistantMessage];
    setMessages(finalMessages);
    setLoading(false);
    
    await saveChatHistory();
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentChatId(generateChatId());
  };

  const deleteChat = async (chatId) => {
    try {
      const command = new DeleteCommand({
        TableName: CONFIG.DYNAMODB_TABLE,
        Key: {
          user_id: user.id,
          chat_id: chatId
        }
      });
      await clients.dynamodb.send(command);
      
      // Remove from local state
      setChatHistory(chatHistory.filter(chat => chat.chat_id !== chatId));
      
      // If current chat is deleted, clear messages
      if (currentChatId === chatId) {
        setMessages([]);
        setCurrentChatId(generateChatId());
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const loadChat = (chat) => {
    setMessages(chat.messages || []);
    setCurrentChatId(chat.chat_id);
    setSidebarOpen(false); // Close sidebar on mobile after loading chat
  };

  if (!authenticated) {
    return (
      <div className="login-container">
        <div className="login-card">
          <img 
            src={CONFIG.LOGO_PATH} 
            alt="Logo" 
            className="login-logo"
          />
          <h1>{CONFIG.APP_NAME}</h1>
          <p>เข้าสู่ระบบเพื่อเริ่มใช้งาน AI แชทบอท</p>
          <button onClick={handleLogin} className="login-btn">
            เข้าสู่ระบบด้วย Cognito
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-content">
          <div className="section">
            <h3>
              🤖 เลือกโมเดล AI
              {modelsLoading && <span className="loading-spinner">●●●</span>}
            </h3>
            <select 
              value={selectedModel} 
              onChange={(e) => setSelectedModel(e.target.value)} 
              className="model-select"
              disabled={modelsLoading}
            >
              {modelsLoading ? (
                <option disabled>กำลังโหลดโมเดล...</option>
              ) : models.length === 0 ? (
                <option disabled>ไม่พบโมเดล</option>
              ) : (
                <>
                  <option value="">เลือกโมเดล AI</option>
                  {Object.entries(
                    models.reduce((groups, model) => {
                      const provider = model.providerName;
                      if (!groups[provider]) groups[provider] = [];
                      groups[provider].push(model);
                      return groups;
                    }, {})
                  ).map(([provider, providerModels]) => (
                    <optgroup key={provider} label={provider}>
                      {providerModels.map(model => (
                        <option key={model.modelId} value={model.modelId}>
                          {model.modelName}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </>
              )}
            </select>
            {modelsLoading && (
              <p className="loading-text">กำลังโหลดโมเดล AI จาก Amazon Bedrock...</p>
            )}
          </div>

          <div className="section">
            <h3>
              📚 เลือก Knowledge Base
              {kbLoading && <span className="loading-spinner">●●●</span>}
            </h3>
            <select 
              value={selectedKnowledgeBase} 
              onChange={(e) => setSelectedKnowledgeBase(e.target.value)} 
              className="model-select"
              disabled={kbLoading}
            >
              {kbLoading ? (
                <option disabled>กำลังโหลด Knowledge Base...</option>
              ) : (
                <>
                  <option value="">ไม่ใช้ Knowledge Base</option>
                  {knowledgeBases.map(kb => (
                    <option key={kb.knowledgeBaseId} value={kb.knowledgeBaseId}>
                      {kb.name}
                    </option>
                  ))}
                </>
              )}
            </select>
            {kbLoading && (
              <p className="loading-text">กำลังโหลด Knowledge Base จาก Bedrock...</p>
            )}
          </div>

          <div className="section">
            <h3>📚 ประวัติการสนทนา</h3>
            <div className="chat-history">
              {chatHistory.length === 0 ? (
                <p className="no-history">ยังไม่มีประวัติการสนทนา</p>
              ) : (
                chatHistory.map(chat => (
                  <div key={chat.chat_id} className="chat-item-container">
                    {editingChatId === chat.chat_id ? (
                      <div className="chat-edit-container">
                        <input
                          type="text"
                          value={editingChatName}
                          onChange={(e) => setEditingChatName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && saveChatName()}
                          className="chat-name-input"
                          autoFocus
                        />
                        <button onClick={saveChatName} className="save-chat-btn">✓</button>
                        <button onClick={cancelEditingChat} className="cancel-chat-btn">✕</button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => loadChat(chat)}
                          className="chat-item"
                        >
                          💬 {chat.chat_name || chat.messages[0]?.content.substring(0, 25) + '...'}
                        </button>
                        <button
                          onClick={() => startEditingChat(chat.chat_id, chat.chat_name || chat.messages[0]?.content.substring(0, 25))}
                          className="edit-chat-btn"
                          title="แก้ไขชื่อ"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => deleteChat(chat.chat_id)}
                          className="delete-chat-btn"
                          title="ลบการสนทนา"
                        >
                          ลบ
                        </button>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="user-info">
          <div className="user-avatar">👤</div>
          <div className="user-details">
            <div className="chat-controls">
              <button onClick={startNewChat} className="chat-control-btn new-chat">
                แชทใหม่
              </button>
              <button onClick={() => setMessages([])} className="chat-control-btn clear">
                ล้างประวัติ
              </button>
            </div>
            <p className="user-email">{user.email}</p>
            <button onClick={() => {
              // Clear local state and storage
              setAuthenticated(false);
              setUser(null);
              localStorage.removeItem('mbk_user');
              
              // Redirect to Cognito logout
              const logoutUrl = `${CONFIG.COGNITO_DOMAIN}/logout?client_id=${CONFIG.CLIENT_ID}&logout_uri=${encodeURIComponent(window.location.origin + '/')}`;
              window.location.href = logoutUrl;
            }} className="logout-btn">
              🚪 ออกจากระบบ
            </button>
          </div>
        </div>
      </div>

      <div className="main-chat">
        <div className="chat-header">
          <div className="header-left">
            <button 
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              ☰
            </button>
            <img 
              src={CONFIG.LOGO_PATH} 
              alt="Logo" 
              className="header-logo"
            />
            <h2>{CONFIG.APP_NAME}</h2>
          </div>
          <span className="model-indicator">
            {modelsLoading 
              ? 'กำลังโหลดโมเดล...'
              : selectedModel 
                ? models.find(m => m.modelId === selectedModel)?.modelName || 'โมเดลที่เลือก'
                : 'ยังไม่ได้เลือกโมเดล'
            }
          </span>
        </div>

        <div className="messages">
          {modelsLoading ? (
            <div className="loading-message">
              <div className="loading-icon">🤖</div>
              <h3>กำลังเตรียมระบบ AI</h3>
              <p>กำลังโหลดโมเดล AI จาก Amazon Bedrock</p>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="welcome-message">
              <h3>👋 สวัสดีครับ!</h3>
              <p>ยินดีต้อนรับสู่ MBK Internal GenAI</p>
              {!selectedModel ? (
                <p className="instruction">กรุณาเลือกโมเดล AI จากแถบด้านซ้ายเพื่อเริ่มสนทนา</p>
              ) : (
                <p className="instruction">พิมพ์ข้อความเพื่อเริ่มสนทนากับ {models.find(m => m.modelId === selectedModel)?.modelName}</p>
              )}
            </div>
          ) : (
            messages.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                <div className="message-avatar">
                  {message.role === 'user' ? (
                    <img src={CONFIG.LOGO_PATH} alt="User" className="avatar-img" />
                  ) : (
                    <img src={CONFIG.LOGO_PATH} alt="MBK GenAI" className="avatar-img" />
                  )}
                </div>
                <div className="message-content">
                  <div className="message-header">
                    {message.role === 'user' ? user.email.split('@')[0] : 'MBK GenAI'}
                  </div>
                  <p>{message.content}</p>
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="message assistant">
              <div className="message-avatar">
                <img src={CONFIG.LOGO_PATH} alt="MBK GenAI" className="avatar-img" />
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        {uploadedFile && (
          <div className="file-upload-area">
            <div className={`uploaded-file ${loading ? 'processing' : ''}`}>
              <span className="file-icon">📄</span>
              <span className="file-name">{uploadedFile.name}</span>
              {loading && <span className="processing-text">Processing...</span>}
              <button onClick={removeFile} className="remove-file-btn" disabled={loading}>✕</button>
            </div>
          </div>
        )}

        <div className="input-area">
          <input
            type="file"
            accept=".txt,.md,.html,.pdf,.docx,.doc"
            onChange={handleFileUpload}
            className="file-input"
            id="file-upload"
            style={{ display: 'none' }}
          />
          <label htmlFor="file-upload" className="file-upload-btn">
            📎
          </label>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder={selectedModel ? "พิมพ์ข้อความที่นี่..." : "กรุณาเลือกโมเดล AI ก่อน..."}
            className="message-input"
            disabled={!selectedModel || loading}
          />
          <button 
            onClick={sendMessage} 
            disabled={loading || !inputMessage.trim() || !selectedModel} 
            className="send-btn"
          >
            {loading ? "กำลังส่ง..." : "ส่ง"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
